import { take, tap } from 'rxjs/operators';
import { Validators, FormBuilder } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Component, Input, OnDestroy, OnInit, LOCALE_ID, Output, EventEmitter, ChangeDetectionStrategy, inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { AppFacadeService } from '../../services/app-facade.service';
import { NotificationService } from '../../services/notification.service';
import { ItemOutService } from '../../services/item-out.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '../../services';
import { CallOffRequestResponse, ItemOutCP } from '../../models/item-out-model';
import { RequestActionService } from '../../services/request-action.service';
import { DateUtility } from '../../date.utility';
import { DateFormatType } from '../../enums/date-format-type.enum';
import { Customer } from '../../models/consumer';
import { getCustomer } from '../../store/domains/customer/customer.selectors';
import { AuthUiService } from '../../services/auth-ui.service';
import { CallOffRequest } from '../../models/calloff-request.model';
import { ReturnRequestType } from '../../enums/return-request-type.enum';
import { AppFormValidators } from '../../admin/validators/app.validators';
import { AppMediatorService } from '../../services/app-mediator.service';
import { ContractDetail } from '../../models/contract-model';
import { FormatAddressPipe } from '../../shared/pipes/format-address.pipe';
import { DialogContentType } from '../../enums/dialog-type.enum';

@Component({
    standalone: false,
    selector: 'por-service-request',
    templateUrl: './service-request.component.html',
    styleUrls: ['./service-request.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceRequestComponent implements OnInit, OnDestroy {
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly store = inject(Store);
    private readonly itemOutService = inject(ItemOutService);
    private readonly notification = inject(NotificationService);
    private readonly translateService = inject(TranslateService);
    private readonly logger = inject(LoggerService);
    private readonly requestActionService = inject(RequestActionService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly authService = inject(AuthUiService);
    private readonly fb = inject(FormBuilder);
    private readonly formatAddressPipe = inject(FormatAddressPipe);

    dateFormat: string;
    isStepValid = false;
    @Input() uiUrl: string | undefined;

    @Output()
    readonly isFormReturn: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output()
    readonly callOffRequestInfo: EventEmitter<CallOffRequest> = new EventEmitter<CallOffRequest>();
    constructor() {
        const locale = inject(LOCALE_ID);

        this.dateFormat = DateUtility.getDateDisplayFormat(DateFormatType.StandardDate, locale);
    }

    enabledTab$ = this.requestActionService.activeTab$;
    returnForm = this.fb.group({
        customerName: ['', [Validators.required, Validators.maxLength(255)]],
        customerPhone: ['', [Validators.required, Validators.maxLength(30)]],
        email: ['', [Validators.required, AppFormValidators.emailValidator, Validators.maxLength(255)]],
        jobSiteName: ['', [Validators.required, Validators.maxLength(255)]],
        jobSitePhone: ['', [Validators.required, Validators.maxLength(30)]],
        gateCombo: ['', [Validators.nullValidator, Validators.maxLength(255)]],
        address: ['', [Validators.required, Validators.maxLength(255)]],
        comments: ['', [Validators.nullValidator]]
    });
    subscriptions: Subscription[] = [];
    @Input() items!: ItemOutCP[];
    @Input() customerId!: string;
    @Input() contractDetail?: ContractDetail | null;
    customer$!: Observable<Customer>;
    customerInfo!: Customer | null;
    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }

        this.returnForm.setValue({
            customerName: '',
            customerPhone: '',
            email: '',
            jobSiteName: '',
            jobSitePhone: '',
            gateCombo: '',
            address: this.getDeliveryAddress(),
            comments: ''
        });
        this.customer$ = this.store.pipe(select(getCustomer));

        this.subscriptions.push(
            this.customer$
                .pipe(
                    take(1),
                    tap(customer => {
                        this.customerInfo = customer;
                    })
                )
                .subscribe()
        );
    }
    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
    }

    get customerEmail() {
        return !this.appFacadeService.versionToggleService.isQuickLinkVersion() ? this.authService.user.Email : '';
    }

    get customerName() {
        return this.returnForm.get('customerName');
    }

    get customerPhone() {
        return this.returnForm.get('customerPhone');
    }

    get email() {
        return this.returnForm.get('email');
    }

    get jobSiteName() {
        return this.returnForm.get('jobSiteName');
    }

    get jobSitePhone() {
        return this.returnForm.get('jobSitePhone');
    }

    get gateCombo() {
        return this.returnForm.get('gateCombo');
    }

    get address() {
        return this.returnForm.get('address');
    }

    get comments() {
        return this.returnForm.get('comments');
    }

    checkSiteInfo() {
        if (this.address?.valid && this.gateCombo?.valid && this.jobSiteName?.valid && this.jobSitePhone?.valid) {
            return true;
        }

        return false;
    }

    sendReqeustForService() {
        this.appFacadeService.setLoading(true);

        if (!this.returnForm.valid) {
            this.notification.error(this.translateService.instant('pleaseFillRequired'));
            this.appFacadeService.setLoading(false);
            return false;
        }

        if (!this.customerId) {
            this.logger.logError('Customer Id Not Found in your request');
            return;
        }

        const now = new Date();
        this.subscriptions.push(
            this.itemOutService
                .calloffrent({
                    items: this.items,
                    customerId: this.customerId,
                    customerName: this.returnForm.value?.customerName,
                    accountName: this.customerInfo?.CompanyName ?? this.customerInfo?.Name,
                    ...this.returnForm.value,
                    // The modal no longer collects date/time; submission time is the request time.
                    requestedDate: now.toISOString(),
                    requestedTime: this.formatTime(now),
                    returnType: ReturnRequestType.Service
                })
                .subscribe({
                    next: (res: CallOffRequestResponse) => {
                        this.appFacadeService.setLoading(false);
                        // WHY: the controller's automated off-rent call (LineItemCallOffRentService)
                        // can fail independently of the legacy request-log/email flow this component's
                        // success path is otherwise built around — without this check the customer sees
                        // a false "success" dialog even when the automated call-off never went through.
                        // HOW: mirrors close-item-out.component.ts's handling of this same
                        // itemOutService.calloffrent response shape. Checked (and callOffRequestInfo
                        // withheld) BEFORE emitting anything — the host handler for that output closes
                        // this component's modal, which would defeat leaving the form up for the renter
                        // to see the error and retry if emit() ran first.
                        const status = res.automatedCallOff?.status;
                        // Allow-list, not an exclusion list: any status this component doesn't
                        // explicitly recognize as a real success is treated as not-confirmed, so a
                        // future status added on the API side doesn't silently read as "succeeded".
                        if (status && status !== 'success' && status !== 'skipped' && status !== 'notImplemented') {
                            this.logger.logError(res.automatedCallOff?.error);
                            this.notification.error(this.translateService.instant('automatedCallOffNotConfirmed'));
                            return;
                        }
                        this.callOffRequestInfo.emit(res.callOffRequest);
                        this.returnForm.reset();
                        this.appFacadeService.openDialog(DialogContentType.RequestSentSuccessfully);
                    },
                    error: err => {
                        this.appFacadeService.setLoading(false);
                        this.logger.alertDevError(err);
                        this.notification.error(this.translateService.instant('somethingWentWrong'));
                    }
                })
        );
        return true;
    }

    getDeliveryAddress(): string {
        if (this.contractDetail?.DriverDelivery?.DeliveryAddress) {
            return this.formatAddressPipe.format(this.contractDetail?.DriverDelivery?.DeliveryAddress);
        }
        return '';
    }

    return() {
        this.isFormReturn.emit(true);
    }

    formatTime(date: Date): string {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }

    isAddressExists(): boolean {
        return !!this.returnForm.get('address')?.value && !!this.getDeliveryAddress();
    }

    // The hint-separating spaces render only alongside the hint, so the required asterisk
    // (`.required label::after`) sits after the words on every line, hint present or not (ADO-124763).
    get equipmentLocationLabel(): string {
        const name = this.translateService.instant('Current Location of Equipment');
        if (!this.isAddressExists()) {
            return name;
        }
        return `${name}\u00A0\u00A0\u00A0 ${this.translateService.instant('Is the equipment still at this address?')}`;
    }
}
